import React from "react";
import useResponsive from "../hooks/useResponsive";
import { getCurrentSemester } from "../../../utils/dateUtils";

const formatDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const SemesterTabs = ({
  semesters,
  selectedSemester,
  onSemesterSelect,
  selectedClass,
}) => {
  const { isMobile } = useResponsive();

  const currentSemesterInfo = selectedClass?.startDate
    ? getCurrentSemester(selectedClass.startDate, selectedClass.professionType)
    : null;

  const isCurrentSemester = (semester) => {
    if (!currentSemesterInfo || !semester?.name) return false;

    const semesterNum = parseInt(semester.name.match(/\d+/)?.[0] || "0", 10);
    return (
      semesterNum === currentSemesterInfo.semesterNumber &&
      currentSemesterInfo.isActive
    );
  };

  if (!selectedClass) {
    return null;
  }

  if (semesters.length === 0) {
    return (
      <div className="card-brand text-center py-6 sm:py-8 mb-6">
        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-calendar-times text-gray-400 text-xl sm:text-2xl"></i>
        </div>
        <p className="text-gray-600 font-medium text-sm sm:text-base">
          No semesters available for this class
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Class Header */}
      <div className="card-brand mb-6">
        <div className="bg-gradient-to-r from-sunrise-red to-sunrise-red/90 text-white px-4 sm:px-6 py-4 sm:py-6 rounded-xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <div className="flex items-center mb-4 sm:mb-0 sm:mr-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center mr-4 sm:mr-6 flex-shrink-0">
                <i className="fas fa-university text-2xl sm:text-3xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2 leading-tight">
                  {selectedClass.schoolName}
                </h2>
                <p className="text-white/90 text-sm sm:text-lg">
                  Class: <span className="font-semibold">{selectedClass.name}</span>
                  {!isMobile &&
                    selectedClass.startDate &&
                    selectedClass.endDate && (
                      <span className="hidden sm:inline">
                        {" "}
                        | Period: {formatDate(selectedClass.startDate)} -{" "}
                        {formatDate(selectedClass.endDate)}
                      </span>
                    )}
                </p>

                {isMobile &&
                  selectedClass.startDate &&
                  selectedClass.endDate && (
                    <p className="text-white/80 text-xs mt-1">
                      {formatDate(selectedClass.startDate)} -{" "}
                      {formatDate(selectedClass.endDate)}
                    </p>
                  )}
              </div>
            </div>

            <div className="w-full sm:w-auto sm:text-right">
              <div className="bg-white/20 px-3 sm:px-4 py-2 rounded-lg">
                <div className="text-xs sm:text-sm text-white/90">Available</div>
                <div className="text-lg sm:text-xl font-bold">
                  {semesters.length}
                  <span className="text-xs sm:text-sm ml-1">
                    semester{semesters.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Semester Tabs */}
      <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
        {isMobile ? (
          /* Mobile: Dropdown */
          <div className="p-4 sm:p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <i className="fas fa-calendar-alt mr-2 text-sunrise-red"></i>
              Select Semester:
            </label>

            <div className="relative">
              <select
                value={selectedSemester?.id || ""}
                onChange={(e) => {
                  const semester = semesters.find(
                    (s) => s.id.toString() === e.target.value,
                  );
                  if (semester) onSemesterSelect(semester);
                }}
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red bg-white text-gray-900 font-medium shadow-sm transition-all duration-200 appearance-none cursor-pointer hover:border-gray-300"
              >
                <option value="" className="text-gray-500">
                  Choose a semester...
                </option>
                {semesters.map((semester) => {
                  const isCurrent = isCurrentSemester(semester);
                  return (
                    <option
                      key={semester.id}
                      value={semester.id}
                      className="py-2"
                    >
                      {semester.name.replace(/Semester/gi, "Sem")}
                      {isCurrent ? " (Current)" : ""}
                    </option>
                  );
                })}
              </select>

              <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                <i className="fas fa-chevron-down text-gray-400"></i>
              </div>
            </div>

            {selectedSemester && (
              <div className="mt-3 p-3 bg-sunrise-red/10 border border-sunrise-red/20 rounded-lg">
                <div className="flex items-center text-sunrise-red text-sm">
                  <i className="fas fa-info-circle mr-2"></i>
                  <span className="font-medium">
                    Selected: {selectedSemester.name.replace(/Semester/gi, "Sem")}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Desktop + Tablet: Always 2 rows, max 4 per row */
          <div className="p-4 sm:p-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {semesters.map((semester) => {
                const isCurrent = isCurrentSemester(semester);
                const isSelected = selectedSemester?.id === semester.id;

                return (
                  <button
                    key={semester.id}
                    className={`relative min-w-0 px-4 py-4 rounded-xl font-semibold transition-all duration-200 text-sm lg:text-base border ${
                      isSelected
                        ? "bg-sunrise-red text-white border-sunrise-red shadow-md"
                        : isCurrent
                          ? "bg-sunrise-red/5 text-grey-dusk border-sunrise-red/30 hover:bg-sunrise-red/10"
                          : "bg-white text-grey-dusk border-pale-dawn hover:bg-ice-white hover:border-sunrise-red/20"
                    }`}
                    onClick={() => onSemesterSelect(semester)}
                    type="button"
                  >
                    <div className="flex items-center justify-center gap-2">
                      {isCurrent && !isSelected && (
                        <span className="w-2 h-2 rounded-full bg-sunrise-red animate-pulse flex-shrink-0"></span>
                      )}
                      <span className="truncate">
                        {semester.name.replace(/Semester/gi, "Sem")}
                      </span>
                    </div>

                    {isSelected && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full opacity-90"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Message when class is selected but no semester */}
      {selectedClass && !selectedSemester && semesters.length > 0 && (
        <div className="text-center py-8 sm:py-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-cloud/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-calendar-alt text-green-cloud text-xl sm:text-2xl"></i>
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-grey-dusk mb-2">
            Please select a semester
          </h3>
          <p className="text-gray-600 text-sm sm:text-base px-4">
            Choose a semester from the {isMobile ? "dropdown" : "tiles"} above to
            view your subjects and grades.
          </p>
        </div>
      )}
    </div>
  );
};

export default SemesterTabs;